import React, { useState, useEffect } from "react";
import { makeStyles, createStyles } from "@material-ui/styles";
import { ValmetRed6, ValmetGrey10, ValmetGrey4 } from "../../theme";
import { Skeleton } from "@material-ui/lab";
import { Typography } from "@material-ui/core";

const localStyles = makeStyles(() =>
  createStyles({
    input: {
      border: `1px solid ${ValmetGrey10}`,
      boxSizing: "border-box",
      width: "100%",
      outline: "none",
      padding: "5px",
      borderRadius: "3px",
    },
    inputContainer: {
      display: "flex",
      alignItems: "center",
    },
    inputCell: {
      display: "flex",
      alignItems: "center",
      width: "93px",
      marginRight: "4px",
      marginLeft: "10px",
    },
    inputTitleCell: {
      width: "220px",
    },
    inputTitle: {
      color: ValmetGrey4,
      textAlign: "left",
      lineHeight: 1,
      letterSpacing: "0.00938em",
      paddingRight: "10px",
    },
    percentCell: {
      padding: "5px",
      color: ValmetGrey4,
    },
    erroredBorders: {
      border: `1px solid ${ValmetRed6}`,
    },
    erroredText: {
      color: ValmetRed6,
    },
    dividerContainer: {
      margin: "10px 0 10px 0",
    },
    disabledInput: {
      color: ValmetGrey10,
    },
  }),
);

interface Props {
  loading: boolean;
  id: string;
  title: string;
  sign?: string;
  value?: number | null | undefined;
  type?: string;
  isValid?: boolean;
  isDisabled?: boolean;
  step?: number;
  max?: number;
  min?: number;
  handleValueChange?: (value: number) => void;
}

const ValueInput: React.FC<Props> = ({
  loading,
  title,
  value,
  sign,
  isValid = true,
  isDisabled = false,
  max,
  min,
  step,
  handleValueChange,
}) => {
  const localClasses = localStyles();

  const [inputValue, setInputValue] = useState<string>("" + value);

  useEffect(() => {
    setInputValue("" + value);
  }, [value]);

  const checkValueLimits = (value: string): number => {
    const intValue = parseInt(value);

    if (isNaN(intValue)) return min || 0;

    if (max !== undefined && intValue > max) {
      return max;
    }

    if (min !== undefined && intValue < min) {
      return min;
    }

    return intValue;
  };

  const handleInputChange = (value: string) => {
    setInputValue("" + checkValueLimits(value));
  };

  const handleInputBlur = (value: string): void => {
    if (!handleValueChange) return;

    const checkedValue = checkValueLimits(value);

    setInputValue("" + checkedValue);
    handleValueChange(checkedValue);
  };

  return (
    <div className={localClasses.inputContainer}>
      <div className={localClasses.inputTitleCell}>
        <Typography
          variant="body2"
          className={isValid ? localClasses.inputTitle : `${localClasses.inputTitle} ${localClasses.erroredText}`}
        >
          {title}
        </Typography>
      </div>
      <div className={localClasses.inputCell}>
        {loading ? (
          <Skeleton variant="rect" />
        ) : (
          value !== undefined && (
            <>
              <input
                disabled={isDisabled}
                required
                type="number"
                step={step ?? 1}
                value={inputValue}
                onKeyPress={(event) => {
                  event.key === "Enter" && event.preventDefault();
                }}
                onChange={(event) => handleInputChange(event.target.value)}
                onBlur={(event) => handleInputBlur(event.target.value)}
                className={
                  isValid
                    ? isDisabled
                      ? `${localClasses.input} ${localClasses.disabledInput}`
                      : localClasses.input
                    : `${localClasses.input} ${localClasses.erroredBorders} ${localClasses.erroredText}`
                }
              />
              <Typography variant="caption" className={localClasses.percentCell}>
                {sign ?? ""}
              </Typography>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default React.memo(ValueInput);
